import { useState } from "react";
import { Loader2, ChevronRight } from "lucide-react";

const inputCls = "w-full h-14 px-4 text-lg bg-white border border-slate-300 rounded-xl focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 outline-none transition";
const okCls = "w-full h-14 px-4 text-lg bg-emerald-50/40 border border-emerald-500 rounded-xl focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 outline-none transition";
const cls = (ok) => (ok ? okCls : inputCls);
const labelCls = "text-sm font-semibold text-slate-700 mb-2 block";

function Field({ label, id, testid, children, hint, ok, next }) {
  return (
    <div>
      <label className={labelCls} htmlFor={id}>{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
      {ok && next && (
        <div className="flex justify-end mt-1.5">
          <button type="button" data-testid={`next-${id}`} onClick={() => document.getElementById(next)?.focus()}
            className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 flex items-center gap-1">
            Siguiente <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

function fmtFecha(v) {
  const d = String(v || "").replace(/\D/g, "").slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join("/");
}

export default function DatosStep({ boot, ubicacion, moto, loading, onSubmit, onBack, value }) {
  const op = boot.opciones;
  const esCeroKm = moto?.anio && Number(moto.anio) >= new Date().getFullYear();
  const [sinRodar, setSinRodar] = useState(value ? Boolean(value.sin_rodar) : Boolean(moto?.sin_rodar));
  const [aDeclarar, setADeclarar] = useState(value ? Boolean(value.sin_rodar) : Boolean(moto?.sin_rodar));
  const [f, setF] = useState({
    tipodoc: value?.tipodoc || "DNI", nrodoc: value?.nrodoc || "", apellido: value?.apellido || "", nombre: value?.nombre || "",
    fec_nac: fmtFecha(value?.fec_nac || ""),
    sexo: value?.sexo || "", est_civil: value?.est_civil || "4", iva: value?.iva || "CF", cuit: value?.cuit || "",
    mail: value?.mail || "", tel_cel: value?.tel_cel || "", calle: value?.calle || "", altura: value?.altura || "", piso: value?.piso || "", depto: value?.depto || "",
    patente: value?.patente === "A/D" ? "" : value?.patente || "", chasis: value?.chasis || "", motor: value?.motor || "", acepta: true,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const fechaOk = f.fec_nac.replace(/\D/g, "").length === 8;
  const docOk = f.tipodoc === "DNI" ? /^\d{7,8}$/.test(f.nrodoc) : f.nrodoc.length >= 6;
  const nombreOk = (s) => { const v = (s || "").trim(); return v.length >= 2 && /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ' -]+$/.test(v) && !/(.)\1{3}/i.test(v); };
  const telDigits = f.tel_cel.replace(/\D/g, "");
  const telOk = telDigits.length >= 10 && new Set(telDigits).size > 1;
  const mailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.mail);
  const patenteOk = (sinRodar && aDeclarar) || /(^[A-Z]{3}\d{3}$)|(^\d{3}[A-Z]{3}$)|(^[A-Z]\d{3}[A-Z]{3}$)|(^[A-Z]{2}\d{3}[A-Z]{2}$)/.test(f.patente.replace(/\s/g, ""));

  const valid = docOk && nombreOk(f.apellido) && nombreOk(f.nombre) && fechaOk && f.sexo && f.est_civil &&
    mailOk && telOk &&
    f.calle && f.altura && patenteOk && f.chasis && f.motor && f.acepta &&
    (f.iva === "CF" || f.cuit);

  const faltantes = [];
  if (!docOk) faltantes.push("documento (7 u 8 digitos)");
  if (!nombreOk(f.apellido) || !nombreOk(f.nombre)) faltantes.push("apellido y nombre reales");
  if (!fechaOk) faltantes.push("fecha de nacimiento (dia/mes/año)");
  if (!f.sexo) faltantes.push("sexo");
  if (!mailOk) faltantes.push("email válido");
  if (!telOk) faltantes.push("celular válido (con código de área, sin 0 ni 15)");
  if (!f.calle || !f.altura) faltantes.push("calle y altura");
  if (!patenteOk) faltantes.push("patente válida (ej: A123BCD, AB123CD o 123ABC)");
  if (!f.chasis) faltantes.push("chasis");
  if (!f.motor) faltantes.push("motor");
  if (f.iva !== "CF" && !f.cuit) faltantes.push("CUIT");
  if (!f.acepta) faltantes.push("consentimiento de datos");

  const submit = () => {
    onSubmit({
      tipodoc: f.tipodoc, nrodoc: f.nrodoc, apellido: f.apellido, nombre: f.nombre,
      provincia: ubicacion?.provincia || value?.provincia || "", localidad: ubicacion?.localidad_desc || value?.localidad || "",
      calle: f.calle, altura: f.altura, piso: f.piso || null, depto: f.depto || null,
      cp: ubicacion?.cp || value?.cp || "", sub_cp: ubicacion?.sub_cp || value?.sub_cp || null,
      tel_cel: f.tel_cel, sexo: f.sexo, est_civil: f.est_civil, nacionalidad: "3",
      iva: f.iva, cuit: f.cuit || null, mail: f.mail,
      fec_nac: f.fec_nac.replace(/\D/g, ""), es_argentino: true, actividad: "5",
      patente: sinRodar && aDeclarar ? "A/D" : f.patente.replace(/\s/g, ""), chasis: f.chasis, motor: f.motor, sin_rodar: sinRodar,
    });
  };

  return (
    <div data-testid="step-datos">
      <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Tus datos</h1>
      <p className="text-slate-500 mb-6">Los necesitamos para emitir la poliza a tu nombre.</p>

      <h2 className="font-heading font-semibold text-slate-800 mb-3">Datos personales</h2>
      <div className="space-y-4 mb-8">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Tipo" id="tipodoc">
            <select id="tipodoc" data-testid="tipodoc-select" className={inputCls} value={f.tipodoc} onChange={(e) => set("tipodoc", e.target.value)}>
              {op.tipo_doc.map((t) => <option key={t.codigo} value={t.codigo}>{t.codigo}</option>)}
            </select>
          </Field>
          <div className="col-span-2">
            <Field label="Numero de documento" id="nrodoc" ok={docOk} next="apellido">
              <input id="nrodoc" data-testid="nrodoc-input" className={cls(docOk)} inputMode="numeric" maxLength={f.tipodoc === "DNI" ? 8 : 11} placeholder="Sin puntos" value={f.nrodoc} onChange={(e) => set("nrodoc", e.target.value.replace(/\D/g, ""))} />
            </Field>
            {f.tipodoc === "DNI" && f.nrodoc && !docOk && <p className="text-xs text-red-600 mt-1" data-testid="nrodoc-error">El DNI debe tener entre 7 y 8 digitos</p>}
          </div>
        </div>
        <Field label="Apellido" id="apellido" ok={nombreOk(f.apellido)} next="nombre"><input id="apellido" data-testid="apellido-input" className={cls(!!f.apellido.trim())} value={f.apellido} onChange={(e) => set("apellido", e.target.value)} /></Field>
        <Field label="Nombre" id="nombre" ok={nombreOk(f.nombre)} next="fec_nac"><input id="nombre" data-testid="nombre-input" className={cls(!!f.nombre.trim())} value={f.nombre} onChange={(e) => set("nombre", e.target.value)} /></Field>
        <Field label="Fecha de nacimiento" id="fec_nac" ok={fechaOk} next="sexo">
          <input id="fec_nac" data-testid="fecnac-input" className={cls(fechaOk)} inputMode="numeric" placeholder="Ej: 08/12/1980" maxLength={10} value={f.fec_nac} onChange={(e) => set("fec_nac", fmtFecha(e.target.value))} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Sexo" id="sexo" ok={!!f.sexo} next="est_civil">
            <select id="sexo" data-testid="sexo-select" className={cls(!!f.sexo)} value={f.sexo} onChange={(e) => set("sexo", e.target.value)}>
              <option value="">Elegi</option>
              {op.sexo.map((s) => <option key={s.codigo} value={s.codigo}>{s.descripcion}</option>)}
            </select>
          </Field>
          <Field label="Estado civil" id="est_civil" ok={!!f.est_civil} next="iva">
            <select id="est_civil" data-testid="estcivil-select" className={cls(!!f.est_civil)} value={f.est_civil} onChange={(e) => set("est_civil", e.target.value)}>
              <option value="">Elegi</option>
              {op.est_civil.map((s) => <option key={s.codigo} value={s.codigo}>{s.descripcion}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Condicion de IVA" id="iva" ok={!!f.iva} next={f.iva !== "CF" ? "cuit" : "tel"}>
          <select id="iva" data-testid="iva-select" className={inputCls} value={f.iva} onChange={(e) => set("iva", e.target.value)}>
            {op.iva.map((i) => <option key={i.codigo} value={i.codigo.trim()}>{i.descripcion}</option>)}
          </select>
        </Field>
        {f.iva !== "CF" && (
          <Field label="CUIT" id="cuit" ok={!!f.cuit} next="tel"><input id="cuit" data-testid="cuit-input" className={cls(!!f.cuit)} inputMode="numeric" placeholder="Sin guiones" value={f.cuit} onChange={(e) => set("cuit", e.target.value.replace(/\D/g, ""))} /></Field>
        )}
      </div>

      <h2 className="font-heading font-semibold text-slate-800 mb-3">Contacto</h2>
      <div className="space-y-4 mb-8">
        <Field label="Celular" id="tel" hint="Ej: 1126493881" ok={telOk} next="mail"><input id="tel" data-testid="tel-input" className={cls(f.tel_cel.replace(/\D/g, "").length >= 10)} inputMode="tel" value={f.tel_cel} onChange={(e) => set("tel_cel", e.target.value.replace(/[^\d+]/g, ""))} /></Field>
        <Field label="Email" id="mail" hint="A este mail puede llegar tu poliza" ok={mailOk} next="calle"><input id="mail" data-testid="mail-input" type="email" className={cls(f.mail.includes("@"))} value={f.mail} onChange={(e) => set("mail", e.target.value)} /></Field>
      </div>

      <h2 className="font-heading font-semibold text-slate-800 mb-3">Domicilio</h2>
      <div className="bg-slate-100 rounded-xl p-3 text-sm text-slate-600 mb-4">
        {ubicacion?.localidad_desc || value?.localidad} (CP {ubicacion?.cp || value?.cp})
      </div>
      <div className="space-y-4 mb-8">
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2"><Field label="Calle" id="calle" ok={!!f.calle.trim()} next="altura"><input id="calle" data-testid="calle-input" className={cls(!!f.calle.trim())} value={f.calle} onChange={(e) => set("calle", e.target.value)} /></Field></div>
          <Field label="Altura" id="altura" ok={!!f.altura} next="patente"><input id="altura" data-testid="altura-input" className={cls(!!f.altura)} inputMode="numeric" value={f.altura} onChange={(e) => set("altura", e.target.value.replace(/\D/g, ""))} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Piso (opcional)" id="piso" ok={!!f.piso} next="depto"><input id="piso" data-testid="piso-input" className={inputCls} value={f.piso} onChange={(e) => set("piso", e.target.value)} /></Field>
          <Field label="Depto (opcional)" id="depto" ok={!!f.depto} next="patente"><input id="depto" data-testid="depto-input" className={inputCls} value={f.depto} onChange={(e) => set("depto", e.target.value)} /></Field>
        </div>
      </div>

      <h2 className="font-heading font-semibold text-slate-800 mb-3">Datos de la moto</h2>
      <div className="space-y-4 mb-8">
        {esCeroKm && (
          <label className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-[20px] p-4 cursor-pointer" data-testid="sin-rodar-toggle">
            <input type="checkbox" data-testid="sin-rodar-checkbox" className="mt-1 w-5 h-5 accent-emerald-600" checked={sinRodar} onChange={(e) => setSinRodar(e.target.checked)} />
            <span className="text-sm text-slate-700"><b>Mi moto es 0km (sin rodar).</b> Si elegis una cobertura contra robo, deberas tener el <b>certificado de no rodamiento</b>.</span>
          </label>
        )}
        <Field label="Patente" id="patente" ok={patenteOk} next="chasis" hint={sinRodar && aDeclarar ? "Se emite como A/D (a declarar). Cuando la patentes, avisanos y la actualizamos en la poliza." : "Sin espacios ni guiones"}>
          <div className="flex items-center gap-3">
            <input id="patente" data-testid="patente-input" className={`${cls((sinRodar && aDeclarar) || f.patente.length >= 6)} flex-1 disabled:bg-slate-100 disabled:text-slate-400`} placeholder={sinRodar && aDeclarar ? "A/D (a declarar)" : "Ej: A123BCD"} value={f.patente} disabled={sinRodar && aDeclarar} onChange={(e) => set("patente", e.target.value.toUpperCase())} />
            {esCeroKm && sinRodar && (
              <label className="flex items-center gap-2 shrink-0 cursor-pointer" data-testid="patente-ad-toggle">
                <input type="checkbox" data-testid="patente-ad-checkbox" className="w-5 h-5 accent-emerald-600" checked={aDeclarar} onChange={(e) => setADeclarar(e.target.checked)} />
                <span className="text-xs font-semibold text-slate-600 leading-tight">A/D<br />a declarar</span>
              </label>
            )}
          </div>
        </Field>
        <Field label="Numero de chasis" id="chasis" ok={!!f.chasis.trim()} next="motor"><input id="chasis" data-testid="chasis-input" className={cls(!!f.chasis.trim())} value={f.chasis} onChange={(e) => set("chasis", e.target.value)} /></Field>
        <Field label="Numero de motor" id="motor" ok={!!f.motor.trim()} next="consent-checkbox"><input id="motor" data-testid="motor-input" className={cls(!!f.motor.trim())} value={f.motor} onChange={(e) => set("motor", e.target.value)} /></Field>
      </div>

      <label className="flex items-start gap-3 bg-white border border-slate-200 rounded-[20px] p-4 mb-6 cursor-pointer">
        <input id="consent-checkbox" type="checkbox" data-testid="consent-checkbox" className="mt-1 w-5 h-5 accent-emerald-600" checked={f.acepta} onChange={(e) => set("acepta", e.target.checked)} />
        <span className="text-sm text-slate-600">Acepto que mis datos personales sean utilizados para emitir mi seguro, segun la Ley 25.326 de Proteccion de Datos Personales. La poliza es emitida por ATM Seguros.</span>
      </label>

      {!valid && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3" data-testid="datos-faltantes">
          Para continuar falta completar: <b>{faltantes.join(", ")}</b>.
        </p>
      )}

      <p className="text-xs text-slate-400 leading-relaxed mb-28" data-testid="datos-ddjj">
        Revisá bien tu información antes de continuar: declarás que los datos ingresados son correctos y revisten carácter de Declaración Jurada. Por seguridad, registramos tu dispositivo e IP.
      </p>

      <div className="fixed bottom-0 left-0 right-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6 bg-white/85 backdrop-blur-xl border-t border-slate-200 sm:border-t-0 sm:bg-transparent sm:backdrop-blur-none z-50">
        <div className="max-w-xl mx-auto flex gap-3 sm:bg-white sm:border sm:border-slate-200 sm:rounded-full sm:p-2 sm:shadow-xl">
          <button data-testid="wizard-back-button" onClick={onBack} className="h-14 px-6 rounded-[14px] border border-slate-300 text-slate-700 font-medium transition">Atras</button>
          <button data-testid="datos-next-button" disabled={!valid || loading} onClick={submit}
            className="flex-1 h-14 rounded-[14px] bg-[#1C1C1E] hover:bg-zinc-800 disabled:bg-slate-300 text-white font-heading font-semibold text-lg transition flex items-center justify-center gap-2">
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            {loading ? "Procesando..." : "Continuar"}
          </button>
        </div>
      </div>
    </div>
  );
}
